import type { Row, SqlValue } from "@homing/core/client";
import { useEffect, useState, useSyncExternalStore } from "react";
import type { DbState, MailDb } from "./client.ts";

export function useDbState(db: MailDb): DbState {
  return useSyncExternalStore(db.state.subscribe, db.state.get);
}

/**
 * A live query against the local DB: runs once the account's DB is open, and again
 * whenever the worker reports a change to one of `tables`. Never touches the network.
 * Keeps the previous rows while re-querying, so lists don't flash.
 */
export function useQuery<T extends Row>(db: MailDb, sql: string, params: SqlValue[], tables: readonly string[]): { rows: T[] | null; error: string | null } {
  const { account } = useDbState(db);
  const [state, setState] = useState<{ rows: T[] | null; error: string | null }>({ rows: null, error: null });
  const key = JSON.stringify(params);
  const tableKey = tables.join(",");
  useEffect(() => {
    if (!account) return;
    const ac = new AbortController();
    // only the newest query's answer may land
    const gen = { n: 0 };
    const run = () => {
      const mine = ++gen.n;
      const current = () => !ac.signal.aborted && mine === gen.n;
      db.api.query(sql, JSON.parse(key) as SqlValue[]).then(
        (rows) => current() && setState({ rows: rows as T[], error: null }),
        (e: unknown) => current() && setState((s) => ({ rows: s.rows, error: e instanceof Error ? e.message : String(e) })),
      );
    };
    run();
    const want = new Set(tableKey.split(","));
    const unsub = db.onChange((t) => {
      if ([...t].some((x) => want.has(x))) run();
    });
    return () => {
      ac.abort();
      unsub();
    };
  }, [db, account, sql, key, tableKey]);
  return account ? state : { rows: null, error: null };
}
